// AGC: LotTasksCard - Tasks card linked to existing tasks module
// AGC: Shows compact preview with access to full functionality
// AGC: Now includes calendar view toggle (month/week)
// AGC: Updated to match TasksList card patterns (avatar, inline icon, badges)

import React, { useState, useMemo } from 'react'
import { 
  ListTodo, 
  Plus, 
  Calendar, 
  CalendarDays,
  List,
  User, 
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  Clock,
  CheckCircle,
  Sprout,
  Droplets,
  SprayCan,
  FlaskConical,
  Wheat,
  Wrench,
  Eye,
  ClipboardList
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { LotTaskPreview, LotTaskFilters, TaskCategory } from '../../types'

interface LotTasksCardProps {
  lotId: string
  previewTasks: LotTaskPreview[]
  allTasks?: LotTaskPreview[]
  totalTasksCount: number
  activeFilters: LotTaskFilters
  onFilterChange: (filters: LotTaskFilters) => void
  onTaskPress: (taskId: string) => void
  onAddTask: () => void
  onViewAll?: () => void
}

// AGC: View mode type for list vs calendar
type ViewMode = 'list' | 'calendar'
type CalendarMode = 'month' | 'week'

/**
 * AGC: Status indicator colors and icons
 */
const getStatusConfig = (status: LotTaskPreview['status']) => {
  switch (status) {
    case 'overdue':
      return {
        color: 'bg-red-500',
        textColor: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: <AlertCircle className="w-4 h-4" />,
      }
    case 'due_today':
      return {
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: <Clock className="w-4 h-4" />,
      }
    case 'upcoming':
      return {
        color: 'bg-green-500',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <Calendar className="w-4 h-4" />,
      }
    case 'completed':
      return {
        color: 'bg-gray-400',
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
        icon: <CheckCircle className="w-4 h-4" />,
      }
    default:
      return {
        color: 'bg-gray-400',
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
        icon: <Clock className="w-4 h-4" />,
      }
  }
}

/**
 * AGC: Format relative date for task display
 */
const formatTaskDate = (date: Date, status: LotTaskPreview['status'], t: (key: string) => string): string => {
  const now = new Date()
  const taskDate = new Date(date)
  const diffTime = taskDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (status === 'overdue') {
    if (diffDays === -1) return t('lotPage.taskYesterday')
    return `${t('lotPage.taskOverdue')}: ${t('lotPage.daysAgo', { count: Math.abs(diffDays) })}`
  }
  
  if (diffDays === 0) return t('lotPage.taskToday')
  if (diffDays === 1) return t('lotPage.taskTomorrow')
  
  return taskDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

/**
 * AGC: LotTasksCard - Main tasks card component with calendar view
 */
export const LotTasksCard: React.FC<LotTasksCardProps> = ({
  previewTasks,
  allTasks,
  totalTasksCount,
  activeFilters,
  onFilterChange,
  onTaskPress,
  onAddTask,
  onViewAll,
}) => {
  const { t } = useTranslation()

  // AGC: View mode state (list or calendar)
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month')
  const [calendarDate, setCalendarDate] = useState(new Date())
  // AGC: In-place expansion — show all tasks within the card instead of navigating away
  const [showAllTasks, setShowAllTasks] = useState(false)
  const visibleTasks = showAllTasks && allTasks ? allTasks : previewTasks

  // AGC: Calendar helpers
  const calendarMonth = calendarDate.getMonth()
  const calendarYear = calendarDate.getFullYear()
  
  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(calendarYear, calendarMonth, 1).getDay()
  
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
  const dayNames = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

  // AGC: Get tasks grouped by date for calendar
  const tasksByDate = useMemo(() => {
    const grouped = new Map<string, LotTaskPreview[]>()
    
    previewTasks.forEach(task => {
      const date = new Date(task.dueDate)
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      const existing = grouped.get(dateKey) || []
      grouped.set(dateKey, [...existing, task])
    })
    
    return grouped
  }, [previewTasks])

  // AGC: Get week dates for week view
  const getWeekDates = () => {
    const startOfWeek = new Date(calendarDate)
    const dayOfWeek = startOfWeek.getDay()
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek)
    
    const dates: Date[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek)
      date.setDate(date.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  // AGC: Navigation handlers
  const prevPeriod = () => {
    if (calendarMode === 'month') {
      setCalendarDate(new Date(calendarYear, calendarMonth - 1, 1))
    } else {
      setCalendarDate(new Date(calendarDate.getTime() - 7 * 24 * 60 * 60 * 1000))
    }
  }
  
  const nextPeriod = () => {
    if (calendarMode === 'month') {
      setCalendarDate(new Date(calendarYear, calendarMonth + 1, 1))
    } else {
      setCalendarDate(new Date(calendarDate.getTime() + 7 * 24 * 60 * 60 * 1000))
    }
  }

  // AGC: Filter tabs
  const statusFilters: { id: LotTaskFilters['status']; label: string }[] = [
    { id: 'pending', label: t('lotPage.tasksPending') },
    { id: 'completed', label: t('lotPage.tasksCompleted') },
    { id: 'all', label: t('lotPage.tasksAll') },
  ]

  // AGC: Period filters
  const periodFilters: { id: LotTaskFilters['period']; label: string }[] = [
    { id: 'all', label: t('lotPage.tasksAll') },
    { id: 'today', label: t('lotPage.today') },
    { id: 'week', label: t('lotPage.thisWeek') },
    { id: 'month', label: t('lotPage.thisMonth') },
  ]

  // AGC: Task category icon mapping - matching TasksList
  const taskIconMap: Record<TaskCategory | 'default', React.ComponentType<{ className?: string }>> = {
    plantio: Sprout,
    irrigacao: Droplets,
    pulverizacao: SprayCan,
    adubacao: FlaskConical,
    colheita: Wheat,
    manutencao: Wrench,
    monitoramento: Eye,
    outros: ClipboardList,
    default: ListTodo,
  }

  // AGC: Task category colors - matching TasksList
  const taskCategoryColors: Record<TaskCategory | 'default', string> = {
    plantio: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    irrigacao: 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400',
    pulverizacao: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    adubacao: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    colheita: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    manutencao: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
    monitoramento: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    outros: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    default: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400',
  }

  // AGC: Get task icon and colors
  const getTaskIconAndColors = (category?: TaskCategory): { 
    Icon: React.ComponentType<{ className?: string }>
    bgClass: string
    textClass: string 
  } => {
    const cat = category || 'default'
    const fullClass = taskCategoryColors[cat] || taskCategoryColors.default
    const textClass = fullClass.split(' ').filter(c => c.startsWith('text-') || c.includes(':text-')).join(' ')
    
    return {
      Icon: taskIconMap[cat] || taskIconMap.default,
      bgClass: fullClass,
      textClass: textClass || 'text-gray-600 dark:text-gray-400',
    }
  }

  return (
    <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
      {/* AGC: Header with title, view toggle, and add button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <ListTodo className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('lotPage.lotTasks')}
          </h3>
        </div>
        
        <div className="flex items-center gap-2">
          {/* AGC: View toggle (list/calendar) */}
          <div className="flex bg-white dark:bg-slate-700 rounded-lg p-0.5 border border-slate-200 dark:border-slate-600">
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'list' 
                  ? 'bg-slate-100 dark:bg-slate-600 text-indigo-600 dark:text-indigo-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              title={t('tasks.list')}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-1.5 rounded-md transition-all ${
                viewMode === 'calendar' 
                  ? 'bg-slate-100 dark:bg-slate-600 text-indigo-600 dark:text-indigo-400' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              title={t('tasks.calendar')}
            >
              <CalendarDays className="w-4 h-4" />
            </button>
          </div>
          
          {/* AGC: Add task button */}
          <button
            onClick={onAddTask}
            className="p-2 rounded-lg text-white transition-colors"
            style={{ backgroundColor: 'var(--accent-primary)' }}
            aria-label={t('lotPage.addTask')}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* AGC: Filters - only show in list view */}
      {viewMode === 'list' && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
          <select
            value={activeFilters.status}
            onChange={(e) => onFilterChange({ ...activeFilters, status: e.target.value as LotTaskFilters['status'] })}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': 'var(--accent-primary)' } as React.CSSProperties}
          >
            {statusFilters.map((filter) => (
              <option key={filter.id} value={filter.id}>
                {filter.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={activeFilters.period}
              onChange={(e) => onFilterChange({ ...activeFilters, period: e.target.value as LotTaskFilters['period'] })}
              className="bg-transparent text-gray-700 dark:text-gray-300 focus:outline-none"
            >
              {periodFilters.map((filter) => (
                <option key={filter.id} value={filter.id}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* AGC: Calendar View */}
      {viewMode === 'calendar' && (
        <div className="mb-4">
          {/* AGC: Calendar header with month/week toggle */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevPeriod}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white text-sm">
                {calendarMode === 'month' 
                  ? `${monthNames[calendarMonth]} ${calendarYear}`
                  : formatWeekRange(getWeekDates())
                }
              </span>
              
              {/* AGC: Month/Week toggle */}
              <div className="flex bg-slate-200 dark:bg-slate-700 rounded p-0.5 text-xs">
                <button
                  onClick={() => setCalendarMode('month')}
                  className={`px-2 py-0.5 rounded transition-colors ${
                    calendarMode === 'month'
                      ? 'bg-white dark:bg-slate-600 shadow'
                      : 'text-gray-500'
                  }`}
                  style={calendarMode === 'month' ? { color: 'var(--accent-primary)' } : undefined}
                >
                  {t('tasks.month')}
                </button>
                <button
                  onClick={() => setCalendarMode('week')}
                  className={`px-2 py-0.5 rounded transition-colors ${
                    calendarMode === 'week'
                      ? 'bg-white dark:bg-slate-600 shadow'
                      : 'text-gray-500'
                  }`}
                  style={calendarMode === 'week' ? { color: 'var(--accent-primary)' } : undefined}
                >
                  {t('tasks.week')}
                </button>
              </div>
            </div>
            
            <button
              onClick={nextPeriod}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* AGC: Month Calendar Grid */}
          {calendarMode === 'month' && (
            <>
              {/* AGC: Day names header */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {dayNames.map((day, i) => (
                  <div key={i} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* AGC: Calendar days */}
              <div className="grid grid-cols-7 gap-0.5">
                {/* AGC: Empty cells for days before month starts */}
                {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-8" />
                ))}
                
                {/* AGC: Days of month */}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1
                  const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                  const dayTasks = tasksByDate.get(dateStr) || []
                  const isToday = new Date().toISOString().split('T')[0] === dateStr
                  const hasOverdue = dayTasks.some(t => t.status === 'overdue')
                  const hasDueToday = dayTasks.some(t => t.status === 'due_today')
                  
                  return (
                    <button
                      key={day}
                      onClick={() => {
                        if (dayTasks.length > 0) {
                          onTaskPress(dayTasks[0].id)
                        }
                      }}
                      className={`h-8 rounded text-xs transition-colors relative ${
                        isToday 
                          ? 'bg-[var(--accent-light-bg)] border' 
                          : dayTasks.length > 0 
                            ? hasOverdue 
                              ? 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30' 
                              : hasDueToday
                                ? 'bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100'
                                : 'bg-[var(--accent-light-bg)] hover:opacity-80'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                      style={isToday ? { borderColor: 'var(--accent-primary)' } : undefined}
                    >
                      <span 
                        className={`${isToday ? 'font-bold' : 'text-gray-700 dark:text-gray-300'}`}
                        style={isToday ? { color: 'var(--accent-primary)' } : undefined}
                      >
                        {day}
                      </span>
                      {/* AGC: Task dots */}
                      {dayTasks.length > 0 && (
                        <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                          {dayTasks.slice(0, 3).map((task, idx) => (
                            <div
                              key={idx}
                              className={`w-1 h-1 rounded-full ${
                                task.status === 'completed' 
                                  ? 'bg-gray-400' 
                                  : task.status === 'overdue'
                                    ? 'bg-red-500'
                                    : task.status === 'due_today'
                                      ? 'bg-yellow-500'
                                      : ''
                              }`}
                              style={
                                task.status !== 'completed' && task.status !== 'overdue' && task.status !== 'due_today' 
                                  ? { backgroundColor: 'var(--accent-primary)' } 
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {/* AGC: Week Calendar View */}
          {calendarMode === 'week' && (
            <div className="space-y-1">
              {getWeekDates().map((date, i) => {
                const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                const dayTasks = tasksByDate.get(dateStr) || []
                const isToday = new Date().toISOString().split('T')[0] === dateStr
                
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-lg ${
                      isToday 
                        ? 'bg-[var(--accent-light-bg)] border' 
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                    style={isToday ? { borderColor: 'var(--accent-primary)', borderWidth: '1px', borderStyle: 'solid' } : undefined}
                  >
                    <div className="w-10 text-center">
                      <div 
                        className={`text-xs ${isToday ? '' : 'text-gray-500'}`}
                        style={isToday ? { color: 'var(--accent-primary)' } : undefined}
                      >
                        {dayNames[date.getDay()]}
                      </div>
                      <div 
                        className={`text-sm font-medium ${isToday ? '' : 'text-gray-900 dark:text-white'}`}
                        style={isToday ? { color: 'var(--accent-primary)' } : undefined}
                      >
                        {date.getDate()}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {dayTasks.length === 0 ? (
                        <span className="text-xs text-gray-400">-</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {dayTasks.slice(0, 2).map((task) => {
                            const statusConfig = getStatusConfig(task.status)
                            return (
                              <button
                                key={task.id}
                                onClick={() => onTaskPress(task.id)}
                                className={`text-xs px-2 py-0.5 rounded-full truncate max-w-[100px] ${statusConfig.bgColor} ${statusConfig.textColor}`}
                              >
                                {task.title}
                              </button>
                            )
                          })}
                          {dayTasks.length > 2 && (
                            <span className="text-xs text-gray-500">+{dayTasks.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* AGC: Task list - only show in list view */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {visibleTasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('lotPage.noTasks')}</p>
              <button
                onClick={onAddTask}
                className="mt-2 text-sm hover:underline"
                style={{ color: 'var(--accent-primary)' }}
              >
                {t('lotPage.addFirstTask')}
              </button>
            </div>
          ) : (
            visibleTasks.map((task) => {
              const statusConfig = getStatusConfig(task.status)
              const { Icon: TaskIcon, bgClass: taskBgClass, textClass: taskTextClass } = getTaskIconAndColors(task.category as TaskCategory | undefined)
              const assignedName = task.assignedOperator || t('lotPage.unassigned')
              
              return (
                <button
                  key={task.id}
                  onClick={() => onTaskPress(task.id)}
                  className="w-full text-left p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all duration-200 active:scale-[0.98]"
                >
                  {/* AGC: Top row - Avatar + Title with inline icon + Status badge */}
                  <div className="flex items-start gap-3">
                    {/* AGC: Assigned person avatar - matching TasksList */}
                    <div className="flex-shrink-0">
                      {task.assignedOperatorAvatar ? (
                        <img src={task.assignedOperatorAvatar} alt={assignedName} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-300/50 dark:bg-gray-600 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{assignedName.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* AGC: Title with small inline task icon */}
                      <div className="flex items-center gap-1.5">
                        <TaskIcon className={`w-4 h-4 flex-shrink-0 ${taskTextClass}`} />
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                          {task.title}
                        </h4>
                        {task.status === 'overdue' && <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {formatTaskDate(task.dueDate, task.status, t)}
                      </p>
                    </div>
                    
                    {/* AGC: Status badge */}
                    <div className="flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                        {statusConfig.icon}
                      </span>
                    </div>
                  </div>

                  {/* AGC: Bottom row - Assignee name at right */}
                  <div className="flex items-center justify-end mt-2 pl-13">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{assignedName.split(' ')[0]}</span>
                  </div>
                </button>
              )
            })
          )}
        </div>
      )}

      {/* AGC: View all / Collapse button — expands in-place instead of navigating away */}
      {totalTasksCount > previewTasks.length && (
        <button
          onClick={() => (allTasks ? setShowAllTasks(v => !v) : onViewAll?.())}
          className="w-full mt-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1 hover:bg-[var(--accent-light-bg)]"
          style={{ color: 'var(--accent-primary)' }}
        >
          <span>
            {showAllTasks ? t('lotPage.showLessTasks') : `${t('lotPage.viewAllTasks')} (${totalTasksCount})`}
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${showAllTasks ? 'rotate-90' : ''}`} />
        </button>
      )}
    </div>
  )
}

/**
 * AGC: Helper to format week range
 */
function formatWeekRange(dates: Date[]): string {
  if (dates.length < 7) return ''
  const start = dates[0]
  const end = dates[6]
  const startMonth = start.toLocaleDateString('pt-BR', { month: 'short' })
  const endMonth = end.toLocaleDateString('pt-BR', { month: 'short' })
  
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()} - ${end.getDate()} ${startMonth}`
  }
  return `${start.getDate()} ${startMonth} - ${end.getDate()} ${endMonth}`
}

export default LotTasksCard
